import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { videoSDKAdapterAtom } from "../core/atoms";
import { useStoredAudioVideoSettings } from "../core/hooks/useStoredAudioVideoSettings";
import { localAudioContextAtom } from "../core/room/participant/media/atoms";

export function useMuteAudio() {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const getLocalAudioContext = useAtomCallback(
    useCallback((get) => get(localAudioContextAtom), []),
  );
  return useCallback(
    async (shouldReleaseTrack?: boolean): Promise<boolean> => {
      return await getAdapter().muteAudio(
        getLocalAudioContext,
        setLocalAudioContext,
        shouldReleaseTrack,
      );
    },
    [getAdapter, setLocalAudioContext, getLocalAudioContext],
  );
}

export function useUnmuteAudio() {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );
  const getLocalAudioContext = useAtomCallback(
    useCallback((get) => get(localAudioContextAtom), []),
  );
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  return useCallback(async (): Promise<boolean> => {
    return await getAdapter().unmuteAudio(
      getLocalAudioContext,
      setLocalAudioContext,
      setStoredAudioVideoSettings,
    );
  }, [
    getAdapter,
    setLocalAudioContext,
    getLocalAudioContext,
    setStoredAudioVideoSettings,
  ]);
}

export function useSwitchMicrophone() {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );
  const getLocalAudioContext = useAtomCallback(
    useCallback((get) => get(localAudioContextAtom), []),
  );
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();

  return useCallback(
    async (microphoneId: string): Promise<boolean> => {
      return await getAdapter().switchMicrophone(
        microphoneId,
        getLocalAudioContext,
        setLocalAudioContext,
        setStoredAudioVideoSettings,
      );
    },
    [
      getAdapter,
      setLocalAudioContext,
      getLocalAudioContext,
      setStoredAudioVideoSettings,
    ],
  );
}
